import React, { useState, useCallback } from 'react';
import type { DrawingPath, Pin, PhotoAnnotation } from '../../../../types/photo.types';
import { DrawingCanvas } from './DrawingCanvas';
import { AnnotationToolbar, AnnotationTool } from './AnnotationToolbar';
import { PinCard } from './PinCard';
import { CaptureOverlay } from '../shared';
import { VoicePinRecorder } from '../../../shared/voice/VoicePinRecorder';
import { keepSubmittablePins } from './pinFilter';
import './AnnotationToolbar.css';

interface PhotoAnnotationLayerProps {
  taskName: string;
  photoUrl: string;
  canDraw: boolean;
  canPin: boolean;
  canVoicePin: boolean;
  annotationPrompt?: string;
  onComplete: (annotation: Omit<PhotoAnnotation, 'caption'>) => void;
  onSkip: () => void;
  onClose: () => void;
}

function anchorTransform(pin: { y: number }): string {
  return pin.y < 0.33 ? 'translate(-50%, 16px)' : 'translate(-50%, calc(-100% - 28px))';
}

const MAX_PINS = 10;
let pinIdCounter = 0;

export const PhotoAnnotationLayer: React.FC<PhotoAnnotationLayerProps> = ({
  taskName,
  photoUrl,
  canDraw,
  canPin,
  canVoicePin,
  annotationPrompt,
  onComplete,
  onSkip,
  onClose,
}) => {
  const [paths, setPaths] = useState<DrawingPath[]>([]);
  const [pins, setPins] = useState<Pin[]>([]);
  const [activeTool, setActiveTool] = useState<AnnotationTool>(canDraw ? 'draw' : canPin ? 'pin' : null);
  const [color, setColor] = useState('#C2AC8E');
  const [strokeWidth, setStrokeWidth] = useState(2);
  const [editingPinId, setEditingPinId] = useState<string | null>(null);
  const [imgDimensions, setImgDimensions] = useState({ width: 0, height: 0 });

  const handlePathComplete = useCallback((path: DrawingPath) => {
    setPaths(prev => [...prev, path]);
  }, []);

  const handleUndo = useCallback(() => {
    setPaths(prev => prev.slice(0, -1));
  }, []);

  const handleTap = useCallback((x: number, y: number) => {
    if (pins.length >= MAX_PINS) return;
    const newPin: Pin = {
      id: `pin-${++pinIdCounter}`,
      index: pins.length + 1,
      x,
      y,
      label: '',
    };
    setPins(prev => [...prev, newPin]);
    setEditingPinId(newPin.id);
  }, [pins.length]);

  const handlePinSave = useCallback((pinId: string, label: string) => {
    setPins(prev => prev.map(p => p.id === pinId ? { ...p, label } : p));
    setEditingPinId(null);
  }, []);

  const handlePinDelete = useCallback((pinId: string) => {
    setPins(prev => {
      const filtered = prev.filter(p => p.id !== pinId);
      return filtered.map((p, i) => ({ ...p, index: i + 1 }));
    });
    setEditingPinId(null);
  }, []);

  const handleDone = () => {
    onComplete({ drawings: paths, pins: keepSubmittablePins(pins) });
  };

  return (
    <CaptureOverlay taskName={taskName} onClose={onClose}>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', position: 'relative' }}>
        {/* Annotation prompt */}
        {annotationPrompt && (
          <div style={{
            padding: '8px 16px',
            textAlign: 'center',
            fontSize: '13px',
            fontWeight: 500,
            color: 'rgba(255,255,255,0.6)',
          }}>
            {annotationPrompt}
          </div>
        )}

        {/* Photo with annotation overlay */}
        <div style={{ flex: 1, position: 'relative', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <img
            src={photoUrl}
            alt="Annotate this photo"
            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
            onLoad={e => {
              const img = e.target as HTMLImageElement;
              setImgDimensions({ width: img.naturalWidth, height: img.naturalHeight });
            }}
          />

          {/* Drawing + pin canvas overlay */}
          <DrawingCanvas
            width={imgDimensions.width}
            height={imgDimensions.height}
            paths={paths}
            activeTool={activeTool}
            color={color}
            strokeWidth={strokeWidth}
            onPathComplete={handlePathComplete}
            onTap={handleTap}
          />

          {/* Pin markers */}
          {pins.map(pin => (
            <React.Fragment key={pin.id}>
              <div
                className="pa-pin-marker"
                style={{ left: `${pin.x * 100}%`, top: `${pin.y * 100}%` }}
                onClick={() => setEditingPinId(pin.id)}
              >
                {pin.index}
              </div>
              {editingPinId === pin.id && (
                <div
                  className="voice-pin-anchor"
                  style={{
                    position: 'absolute',
                    left: `${pin.x * 100}%`,
                    top: `${pin.y * 100}%`,
                    transform: anchorTransform(pin),
                  }}
                >
                  {canVoicePin ? (
                    <>
                      <VoicePinRecorder
                        initialAudioBlob={pin.audioBlob}
                        initialAudioUrl={pin.audioUrl}
                        initialDuration={pin.audioDuration}
                        onCommit={({ blob, duration }) => {
                          setPins(prev => prev.map(p =>
                            p.id === pin.id ? { ...p, audioBlob: blob, audioDuration: duration } : p,
                          ));
                        }}
                        onDiscard={() => {
                          setPins(prev => prev.map(p =>
                            p.id === pin.id
                              ? { ...p, audioBlob: undefined, audioUrl: undefined, audioDuration: undefined }
                              : p,
                          ));
                        }}
                        onCancel={() => {
                          handlePinDelete(pin.id); // Ghost pin — drop it.
                        }}
                      />
                      {(pin.audioBlob || pin.audioUrl) && (
                        <PinCard
                          pinIndex={pin.index}
                          initialLabel={pin.label}
                          required={false}
                          placeholder="Add a label (optional)…"
                          onSave={label => handlePinSave(pin.id, label)}
                          onDelete={() => handlePinDelete(pin.id)}
                          onClose={() => setEditingPinId(null)}
                        />
                      )}
                    </>
                  ) : (
                    <PinCard
                      pinIndex={pin.index}
                      initialLabel={pin.label}
                      required
                      onSave={label => handlePinSave(pin.id, label)}
                      onDelete={() => handlePinDelete(pin.id)}
                      onClose={() => setEditingPinId(null)}
                    />
                  )}
                </div>
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Toolbar */}
        <AnnotationToolbar
          activeTool={activeTool}
          onToolChange={setActiveTool}
          color={color}
          onColorChange={setColor}
          strokeWidth={strokeWidth}
          onStrokeWidthChange={setStrokeWidth}
          canDraw={canDraw}
          canPin={canPin}
          pathCount={paths.length}
          onUndo={handleUndo}
          onDone={handleDone}
        />
      </div>
    </CaptureOverlay>
  );
};
